import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { submitContact, submitOwnerInquiry } from "../lib/api";

const initialContact = { full_name: "", email: "", phone: "", subject: "General Inquiry", message: "" };
const initialOwner = { full_name: "", email: "", phone: "", property_address: "", property_type: "Apartment", message: "" };

export default function Contact() {
  const [tab, setTab] = useState("guest");
  const [c, setC] = useState(initialContact);
  const [o, setO] = useState(initialOwner);
  const [submitting, setSubmitting] = useState(false);

  const onSubmitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(c);
      toast.success("Message sent — we'll respond within one business day.");
      setC(initialContact);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  const onSubmitOwner = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitOwnerInquiry(o);
      toast.success("Inquiry received — a portfolio director will reach out shortly.");
      setO(initialOwner);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505] text-white -mb-24">
      <PageHeader
        overline="Get in Touch"
        title="Speak to a concierge."
        subtitle="Whether you're booking a stay or considering listing your property, our team responds within one business day."
        image="https://images.pexels.com/photos/14765824/pexels-photo-14765824.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
      />

      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* INFO COLUMN */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="card-flat p-8">
              <p className="overline mb-5">Direct Lines</p>
              <ul className="space-y-5 text-sm">
                <li className="flex items-start gap-3"><Phone size={16} className="text-gold mt-0.5 flex-shrink-0" />
                  <a data-testid="contact-info-phone" href="tel:+447474759947" className="text-white/80 hover:text-gold">+44 07474 759947</a>
                </li>
                <li className="flex items-start gap-3"><Mail size={16} className="text-gold mt-0.5 flex-shrink-0" />
                  <a data-testid="contact-info-email" href="mailto:Westlondonstaysltd@gmail.com" className="text-white/80 hover:text-gold">Westlondonstaysltd@gmail.com</a>
                </li>
                <li className="flex items-start gap-3"><MapPin size={16} className="text-gold mt-0.5 flex-shrink-0" />
                  <span data-testid="contact-info-address" className="text-white/70">Hammersmith W6<br />London, United Kingdom</span>
                </li>
                <li className="flex items-start gap-3"><MessageCircle size={16} className="text-emerald mt-0.5 flex-shrink-0" />
                  <a data-testid="contact-info-whatsapp" href="https://wa.me/447474759947" target="_blank" rel="noreferrer" className="text-white/80 hover:text-gold">WhatsApp us</a>
                </li>
              </ul>
            </div>
            <div className="card-flat p-8">
              <p className="overline mb-3">Office Hours</p>
              <p className="text-white/65 text-sm leading-relaxed">
                Mon – Fri · 9:00 – 19:00 GMT<br />
                Sat – Sun · 10:00 – 17:00 GMT<br />
                <span className="text-gold">Concierge: 24/7 for guests in residence</span>
              </p>
            </div>
            <div className="overflow-hidden border border-white/10">
              <iframe
                title="Google Maps"
                data-testid="google-map"
                className="w-full h-64 grayscale-[0.3] contrast-[1.1]"
                src="https://www.google.com/maps?q=Hammersmith+London+W6&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          {/* FORMS COLUMN */}
          <div className="lg:col-span-8">
            <div className="flex border-b border-white/10 mb-8">
              <button
                data-testid="tab-guest"
                onClick={() => setTab("guest")}
                className={`px-6 py-4 text-xs uppercase tracking-[0.22em] font-medium border-b-2 transition-colors ${tab === "guest" ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}
              >
                Guest / General
              </button>
              <button
                data-testid="tab-owner"
                onClick={() => setTab("owner")}
                className={`px-6 py-4 text-xs uppercase tracking-[0.22em] font-medium border-b-2 transition-colors ${tab === "owner" ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}
              >
                List Your Property
              </button>
            </div>

            {tab === "guest" && (
              <form onSubmit={onSubmitContact} data-testid="contact-form" className="card-flat p-10 space-y-5">
                <h2 className="font-serif text-3xl text-white mb-2">How can we help?</h2>
                <p className="text-white/55 text-sm mb-6">Send us a message and our concierge will respond within one business day.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input data-testid="contact-name" required name="full_name" value={c.full_name} onChange={(e) => setC({ ...c, full_name: e.target.value })} placeholder="Full name" className="luxe-input" />
                  <input data-testid="contact-email" required name="email" type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} placeholder="Email" className="luxe-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input data-testid="contact-phone" name="phone" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} placeholder="Phone (optional)" className="luxe-input" />
                  <select data-testid="contact-subject" name="subject" value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })} className="luxe-input">
                    <option>General Inquiry</option>
                    <option>Booking Question</option>
                    <option>Press &amp; Media</option>
                    <option>Careers</option>
                  </select>
                </div>
                <textarea data-testid="contact-message" required rows="5" name="message" value={c.message} onChange={(e) => setC({ ...c, message: e.target.value })} placeholder="Tell us a little about how we can help..." className="luxe-input resize-none" />
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-gold">
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}

            {tab === "owner" && (
              <form onSubmit={onSubmitOwner} data-testid="owner-form" className="card-flat p-10 space-y-5 border-l-2 border-l-emerald">
                <h2 className="font-serif text-3xl text-white mb-2">List your property</h2>
                <p className="text-white/55 text-sm mb-6">Schedule a private consultation with one of our portfolio directors.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input data-testid="owner-name" required name="full_name" value={o.full_name} onChange={(e) => setO({ ...o, full_name: e.target.value })} placeholder="Full name" className="luxe-input" />
                  <input data-testid="owner-email" required name="email" type="email" value={o.email} onChange={(e) => setO({ ...o, email: e.target.value })} placeholder="Email" className="luxe-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input data-testid="owner-phone" required name="phone" value={o.phone} onChange={(e) => setO({ ...o, phone: e.target.value })} placeholder="Phone" className="luxe-input" />
                  <select data-testid="owner-type" name="property_type" value={o.property_type} onChange={(e) => setO({ ...o, property_type: e.target.value })} className="luxe-input">
                    <option>Apartment</option>
                    <option>Penthouse</option>
                    <option>Townhouse</option>
                    <option>Mews House</option>
                    <option>Other</option>
                  </select>
                </div>
                <input data-testid="owner-address" required name="property_address" value={o.property_address} onChange={(e) => setO({ ...o, property_address: e.target.value })} placeholder="Property address (Postcode is sufficient)" className="luxe-input" />
                <textarea data-testid="owner-message" rows="5" name="message" value={o.message} onChange={(e) => setO({ ...o, message: e.target.value })} placeholder="Anything we should know about your property?" className="luxe-input resize-none" />
                <button data-testid="owner-submit" type="submit" disabled={submitting} className="btn-emerald">
                  {submitting ? "Sending..." : "Request Consultation"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
